import moment from 'moment';
import messages from 'string-translations/translations';
import placeholders from 'string-translations/placeholders';
import { formatMessage } from 'i18n/CurrentLocale';
import {
    ClaimState,
    DeviceMode,
    LocalizedLicensePlate,
    PagingReactTable,
    QueryConfig,
    SessionDTOExtended,
    SessionRight,
    Vehicle,
    VehicleAndDriver
} from 'types';
import { SessionExportField } from 'screens/modals/sessionExport/ExportParkingHistoryModal';
import { captureException, captureMessage } from '@sentry/react';
import countries, { Country } from 'world-countries';
import { TELEMATIC_TYPE_APP } from "./constants";
import { ColumnFilter, ColumnSort } from '@tanstack/react-table';


// ==============================================================================================

export function getObjectAsArray(object: any) {
    let array = [];
    for (let item in object) {
        array.push(item);
    }
    return array;
}

//================================================================================================

export function displayTime(timestamp?: string): string {
    return timestamp
        ? moment(timestamp).format('HH:MM')
        : ''
}

export function displayParkingSessionDates(startTime: string | null, endTime: string | null) {
    if (startTime && !endTime) {
        return moment(startTime).format('D MMMM YYYY');
    } else {
        if (moment(startTime).isSame(endTime, 'day')) {
            return moment(endTime).format('D MMMM YYYY');
        } else {
            if (moment(startTime).isSame(endTime, 'month')) {
                return moment(startTime).format('D') + '-' + moment(endTime).format('Do MMMM YYYY');
            } else {
                if (moment(startTime).isSame(endTime, 'year')) {
                    return moment(startTime).format('D MMMM') + ' - ' + moment(endTime).format('D MMMM YYYY');
                } else {
                    return moment(startTime).format('D MMMM YYYY') + ' - ' + moment(endTime).format('D MMMM YYYY');
                }
            }
        }
    }
}

//================================================================================================

export const formatMinutesDuration = (minutes: number | null): string => {
    if (minutes === null) {
        return '';
    }

    try {
        const numberOfDays = Math.floor(minutes / (24 * 60));
        const numberOfHours = Math.floor((minutes % (24 * 60)) / 60);
        const numberOfMinutes = minutes % 60;

        const parts: string[] = [];
        if (numberOfDays > 0) {
            parts.push(`${formatMessage({ id: 'DURATION_FORMATTER_PART_DAYS' }, { days: numberOfDays })}`);
        }
        if (numberOfHours > 0) {
            parts.push(`${formatMessage({ id: 'DURATION_FORMATTER_PART_HOURS' }, { hours: numberOfHours })}`);
        }
        if (numberOfMinutes > 0 || parts.length === 0) {
            parts.push(`${formatMessage({ id: 'DURATION_FORMATTER_PART_MINUTES' }, { minutes: numberOfMinutes })}`);
        }

        return parts.join(', ');
    } catch (e) {
        console.error(`Failed to format duration in minutes ${minutes}`, e);
        return '';
    }
}


export const calculateSessionDuration = (s: SessionDTOExtended): number | null => {
    if (s.internalStartTime && s.internalEndTime && new Date(s.internalStartTime) <= new Date(s.internalEndTime)) {
        let startTimeWithoutSeconds = moment(s.internalStartTime).seconds(0).milliseconds(0).toISOString();
        let endTimeWithoutSeconds = moment(s.internalEndTime).seconds(0).milliseconds(0).toISOString();

        return moment(endTimeWithoutSeconds).diff(moment(startTimeWithoutSeconds), 'minutes');
    }
    return null;
}


// TODO: add tests! + handle timezones?
export const calculateActiveSessionDuration = (startTime: string): string => {
    if (startTime) {
        try {
            const now = moment(new Date(Date.now()));
            const durationInMinutes = moment(now).diff(moment(startTime), 'minutes');

            return formatMinutesDuration(durationInMinutes);
        } catch (e) {
            console.error(e);
            captureException(e);
        }
    }
    return '';
}

//================================================================================================

export const convertCentsToEuroValuta = (cents: number): string => {
    if (typeof cents === 'number') {
        return `€ ${(cents / 100).toFixed(2)}`;
    } else {
        return '?';
    }
};

//================================================================================================

export const generateAllQueryParams = (queryConfig: QueryConfig): string => {
    const { paging, filters, sorting } = queryConfig;
    const filterQueryParams = generateFilterQueryParams(filters);
    const pagingQueryParams = generatePagingQueryParams(paging);
    const sortingQueryParams = generateSortingQueryParams(sorting);
    return filterQueryParams + pagingQueryParams + sortingQueryParams;
}

export function generateFilterQueryParams(filters: ColumnFilter[]): string {
    if (!filters) return '?';

    let filterQueryParams = '?';
    if (filters?.length) {
        filters.forEach((filter) => {
            if (isDefined(filter?.value)) {
                filterQueryParams += `${filter.id}=${filter.value}&`;
            }
        });
    }
    return filterQueryParams;
}

export const getColumnFilter = (filters: ColumnFilter[], filterProperty: string): ColumnFilter | undefined => {
    return filters.find((cf) => cf.id === filterProperty);
}

export function generatePagingQueryParams(paging: PagingReactTable): string {
    return `page=${paging.currentPage}&limit=${paging.pageSize}&`;
}

export function generateSortingQueryParams(sortingProperties: ColumnSort[]): string {
    let sortingParams = '';
    sortingProperties.forEach((sortingProperty: ColumnSort) => {
        sortingParams += 'sort=' + sortingProperty.id + ',' + (sortingProperty.desc ? 'DESC' : 'ASC') + '&';
    });
    return sortingParams;
}

export function generateFieldsQueryParams(fields: SessionExportField[]): string {
    let fieldsParams = `&fields=`;
    fields
        .map(({ value }, index) =>
            (index === fields.length - 1 ? (fieldsParams += `${value}`) : (fieldsParams += `${value},`))
        );
    return fieldsParams;
}

//================================================================================================

export const stripStringAllowNumbersOnly = (string: string) => string.replace(/[^0-9]/g, '');

export const stripStringAfterEmptySpace = (statusString: string) => statusString.split(' ').shift();

export const stripStringFromNonAlphaNumericCharacters = (string: string) => string.replace(/[^a-z0-9]/gi, '');

export const stripStringFromNonAlphaNumericCharactersAllowSeparators = (string: string) =>
    string.replace(/[^a-z0-9\r\n\t\f\v, ]/gi, '').toUpperCase();

export const removeSpaces = (string: string) => string.replace(/\s/g, '');

export const isDefined = (string: unknown) => string !== undefined && string !== null && string !== '';

//================================================================================================

export function isNumericKey(keyCode: number) {
    return keyCode >= 48 && keyCode <= 57;
}

export function isNumpadKey(keyCode: number) {
    return keyCode >= 96 && keyCode <= 105;
}

export function isAlphaKey(keyCode: number) {
    return keyCode >= 65 && keyCode <= 90;
}

export function isOtherAllowedKey(keyCode: number) {
    const otherAllowedKeyCodes = [8, 13, 32, 37, 38, 39, 40, 46, 188]; // backspace, enter, space, left, up, right, down, delete, comma
    return otherAllowedKeyCodes.includes(keyCode);
}

export function isAllowedKeyCodeForLicensePlateImport(keyCode: number) {
    return isNumericKey(keyCode) || isNumpadKey(keyCode) || isAlphaKey(keyCode) || isOtherAllowedKey(keyCode);
}

//================================================================================================

export function detectBrowserLanguage() {
    let browserLanguage;
    const fallbackLanguage = 'en';
    //@ts-ignore
    browserLanguage = window?.window?.navigator?.language || window?.navigator?.userLanguage || fallbackLanguage;
    return browserLanguage.substring(0, 2); // en-GB,
}

//================================================================================================

export function filterContains(filterValue: any, rowValue: any) {
    return rowValue.toLowerCase().includes(filterValue.toLowerCase());
}

//================================================================================================

export function translationExists(messageKeyId: any): boolean {
    //@ts-ignore
    return !!messages['en'][messageKeyId];
}

//================================================================================================

export function getRepresentiveColorForPercentage(percentage: number) {
    if (percentage < 25) {
        return '#D34B35'; // red
    } else if (percentage < 50) {
        return '#FFB200'; // orange
    } else if (percentage < 75) {
        return '#fed031'; // yellow
    } else {
        return '#35D38C'; // green
    }
}

//================================================================================================

export function sleep(ms: number) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

//================================================================================================

export function checkIfCompanyHasAppAccount(telematicAccounts: any) {
    for (var i = 0; i < telematicAccounts.length; i++) {
        if (telematicAccounts[i]?.type === TELEMATIC_TYPE_APP) {
            return true;
        }
    }

    return false;
}

//================================================================================================

export function isSharedVehicle(parkingMode: DeviceMode) {
    return parkingMode === 'SHARED' || parkingMode === 'MANUAL_SHARED';
}

export function isClaimedVehicle(claimState: ClaimState, user: any) {
    return claimState === 'CLAIMED' && user;
}

export function isUnclaimedVehicle(claimState: ClaimState) {
    return claimState === 'UNCLAIMED';
}

export function isUnclaimableVehicle(claimState: ClaimState) {
    return claimState === 'UNCLAIMABLE'; // autonomous
}

//================================================================================================
// todo: loop over COUNTRY_CODE enum, write test to check if translations exist for all countryCodes
export function getTranslatedSupportedCountryLabels() {
    return {
        NL: formatMessage(placeholders.netherlands),
        BE: formatMessage(placeholders.belgium),
    };
}

export function getCountryCodeLabels() {
    return countries.reduce((acc, country) => {
        acc[country.cca2] = country.cca2;
        return acc;
    }, {});
}

//================================================================================================

export function isHostedOnProductionURL() {
    return window.location.hostname === 'my.parkd.com';
}

//================================================================================================

export function isProductionEnvironment() {
    return import.meta.env.VITE_API_ENDPOINT === 'https://api.parkd.com';
}

//================================================================================================

export function isLocalHost() {
    return window.location.hostname === 'localhost';
    //return import.meta.env.VITE_API_ENDPOINT === undefined;
}

//=================================================================================================

export function sortCountries(a: Country, b: Country) {
    return a.cca2 === 'NL' ? -1 : b.cca2 === 'NL' ? 1 : a.cca2 === 'BE' ? -1 : b.cca2 === 'BE' ? 1 : 0;
}

//====================================================================================================

export function removeVehicleFromArray(vehicles: VehicleAndDriver[], deletedVehicle: Vehicle): VehicleAndDriver[] {
    return vehicles.filter(
        (vehicle) => vehicle.vehicle.localizedLicensePlate.licensePlate !== deletedVehicle.localizedLicensePlate.licensePlate
    );
}

export function isSameLicensePlate(lp_A: LocalizedLicensePlate, lp_b: LocalizedLicensePlate): boolean {
    return lp_A.licensePlate === lp_b.licensePlate && lp_A.countryCode === lp_b.countryCode;
}


export const isFreeSessionRight = (right: SessionRight): boolean =>
    right.paymentType === 'FREE' ||
    (right.paymentType === 'PAID' && right.cost === 0 && right.durationType === 'VARIABLE') ||
    (right.paymentType === 'PAID' && right.cost === 0 && right.durationType === null)

export const isVariablePaidRight = (right: SessionRight): boolean =>
    right.paymentType === 'PAID' && right.durationType === 'VARIABLE' && right.cost !== 0;

export const isFixedDurationRight = (right: SessionRight): boolean => right.paymentType === 'PAID' && right.durationType === 'FIXED';

export const isChildFDRRight = (session: SessionDTOExtended): boolean => {
    return isFixedDurationRight(session.right) && session.reusedRight;
};

export const filterOutFreeSessions = (sessions: SessionDTOExtended[]) => {
    return sessions.filter((session: SessionDTOExtended) => !isFreeSessionRight(session.right));
};

